import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  Table,
  Icon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Spinner,
  Select,
  Input,
  Box,
} from "@chakra-ui/react";
import {
  useSortBy,
  usePagination,
  useGlobalFilter,
  useTable,
} from "react-table";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import { Link } from "react-router-dom";
import axios from "axios";
import Card from "components/card/Card";
import { constants } from "constants/constants";

export default function AmazonOrdersTable() {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [days, setDays] = useState("7");
  const [status, setStatus] = useState("");

const columnsData = useMemo(() => [
  {
    Header: "Order Date",
    accessor: "OrderDate",
  },
  {
    Header: "Amazon Order No",
    accessor: "AmazonOrderNo",
     Cell: ({ value }) => (
      <Link to={`/amazon-dforder-view?AmazonInvoiceNo=${value}`}>
        <Text color="blue.500" textDecoration="underline" fontWeight="bold">
          {value}
        </Text>
      </Link>
    ),
  },
  {
    Header: "Jiwa Order No",
    accessor: "JiwaOrderNo",
    Cell: ({ value }) => (
      <Link to={`/amazon-dforder-view?JiwaOrderNo=${value}`}>
        <Text color="blue.500" textDecoration="underline" fontWeight="bold">
          {value}
        </Text>
      </Link>
    ),
  },
  {
    Header: "Status",
    accessor: "OrderStatus",
  },
  {
    Header: "Amazon Total",
    accessor: "AmazonOrderTotal",
    Cell: ({ value }) => (
      <Text>${Number(value).toFixed(2)}</Text>
    ),
  },
  {
    Header: "Davka Total",
    accessor: "DavkaOrderTotal",
    Cell: ({ value }) => (
      <Text>${Number(value).toFixed(2)}</Text>
    ),
  },
], []);


  const fetchAmazonOrders = async () => {
    try {
      setIsDataLoading(true);
      const token = localStorage.getItem("accessToken");

      const response = await axios.get(`${constants.apiUrl}/get-amazon-dforders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          Days: days,
          Status: status,
          JiwaOrderNo: searchQuery || "",
          AmazonInvoiceNo: searchQuery || ""
        },
        withCredentials: true,
      });

      let orders = response.data.records;

      setTableData(orders);
    } catch (error) {
      console.error("Error fetching Amazon orders:", error);

    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    fetchAmazonOrders();
  }, [days, searchQuery, status]);

  const data = useMemo(() => tableData, [tableData]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: columnsData,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const textColor = "gray.900";
  const borderColor = "gray.200";

  return (
    <Card direction="column" w="100%" px="0px" overflowX="auto">
      {isDataLoading ? (
        <Flex height="60vh" alignItems="center" justifyContent="center">
          <Spinner size="xl" thickness="4px" color="green.500" />
        </Flex>
      ) : (
        <Flex direction="column" w="100%">
          <Flex px="25px" justify="space-between" mb="10px" align="center" gap="1rem" flexWrap="wrap">
            {/* Days Filter */}
            <Select
              value={days}
              onChange={(e) => setDays(e.target.value)}
              maxWidth="100px"
              variant="outline"
            >
              {[7, 14, 30, 60].map((d) => (
                <option key={d} value={d}>
                  {d} Days
                </option>
              ))}
            </Select>

            {/* Status Filter */}
            <Select
              placeholder="All Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              maxWidth="150px"
              variant="outline"
            >
              <option value="">All</option>
              <option value="NEW">NEW</option>
              <option value="ACCEPTED">ACCEPTED</option>
              <option value="SHIPPED">SHIPPED</option>
              <option value="CANCELLED">CANCELLED</option>
            </Select>

            <Text color={textColor} fontSize="22px" fontWeight="700">
              Amazon Direct Fulfilment Orders
            </Text>

            <Input
              type="text"
              placeholder="Search by Jiwa Order No"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              maxWidth="250px"
            />
          </Flex>

          <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
            <Thead>
              {headerGroups.map((headerGroup, idx) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                  {headerGroup.headers.map((column, idx2) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={idx2}
                      borderColor={borderColor}
                    >
                      <Flex justify="space-between" align="center">
                        {column.render("Header")}
                        <span>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FiArrowDown} />
                            ) : (
                              <Icon as={FiArrowUp} />
                            )
                          ) : (
                            ""
                          )}
                        </span>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
  {rows.length === 0 ? (
    <Tr>
      <Td colSpan={columnsData.length + 1} textAlign="center">
        No orders found.
      </Td>
    </Tr>
  ) : (
    rows.map((row, i) => {
      prepareRow(row);
      const rowData = row.original;

      // 🔹 Check for issue (from API or derived)
      const hasIssue =
        rowData.HasIssue; // example: total mismatch

      return (
        <Tr
          {...row.getRowProps()}
          key={i}
          bg={hasIssue ? "red.100" : "transparent"} // highlight the row
        >
          {row.cells.map((cell, idx3) => (
            <Td
              {...cell.getCellProps()}
              key={idx3}
              fontSize="sm"
              borderColor="transparent"
              color={hasIssue ? "red.700" : "inherit"}
              fontWeight={hasIssue ? "bold" : "normal"}
            >
              {cell.render("Cell")}
            </Td>
          ))}
        </Tr>
      );
    })
  )}
</Tbody>

          </Table>
        </Flex>
      )}
    </Card>
  );
}
